// Repères de santé publique (sources citées) pour guider les réglages d'objectifs.
// N'inventer aucune valeur : chaque plage porte sa source affichée à l'utilisateur.

import type { TrainingPhase } from './types';

export interface Guideline {
  min: number;
  max: number;
  source: string;
}

// Part des lipides dans l'apport énergétique (% des kcal).
// Plage 20–35 % = EFSA/IOM (l'OMS, elle, plafonne à ≤ 30 %E).
export const FAT_PCT_RECO: Guideline = { min: 20, max: 35, source: 'EFSA/IOM' };

// Acides gras saturés : ≤ 10 % de l'énergie (OMS).
export const SAT_FAT_MAX_PCT = 10;

// Glucides : 45–60 % de l'énergie (EFSA) / 45–65 % (IOM).
export const CARB_PCT_RECO: Guideline = { min: 45, max: 60, source: 'EFSA/IOM' };

// Sucres libres : < 10 % de l'énergie, idéalement < 5 % (OMS).
export const FREE_SUGARS_MAX_PCT = 10;

// Protéines par phase d'entraînement (g/kg/jour), position ISSN 2017 (PMC5477153) :
// - général « construire/maintenir le muscle » : 1.4–2.0 ;
// - restriction énergétique (préserver la masse maigre, entraînés) : 2.3–3.1.
// L'ISSN ne quantifie pas de bande « récupération » distincte → général.
// `default` = milieu de bande (choix UX DANS la plage sourcée, pas un chiffre inventé).
export interface PhaseProteinGuideline extends Guideline {
  default: number;
}
export const PROTEIN_PER_KG_BY_PHASE: Record<TrainingPhase, PhaseProteinGuideline> = {
  cut: {
    min: 2.3,
    max: 3.1,
    default: 2.7,
    source: 'ISSN 2017 — restriction énergétique (PMC5477153)',
  },
  bulk: {
    min: 1.6,
    max: 2.0,
    default: 1.8,
    source: 'ISSN 2017 — général, part haute (PMC5477153)',
  },
  recovery: { min: 1.4, max: 2.0, default: 1.7, source: 'ISSN 2017 — général (PMC5477153)' },
};

export const TRAINING_PHASE_LABELS: Record<TrainingPhase, string> = {
  cut: 'Sèche',
  bulk: 'Prise de masse',
  recovery: 'Récupération musculaire',
};

// Surplus calorique en prise de masse (kcal/jour au-dessus de la maintenance).
// ~+300–500 kcal/j = repère conservateur pour limiter la prise de gras (revue
// hypertrophie, PMC6710320 : ~+360–480 kcal/j ; un surplus plus grand → gras
// disproportionné, Garthe 2013). `default` = 350, valeur prudente en bas de bande.
export interface SurplusGuideline extends Guideline {
  default: number;
}
export const SURPLUS_KCAL_RECO: SurplusGuideline = {
  min: 300,
  max: 500,
  default: 350,
  source: 'revue hypertrophie ~+360–480 kcal/j (PMC6710320)',
};

export const TRAINING_PHASE_ORDER: TrainingPhase[] = ['cut', 'bulk', 'recovery'];

// Rappel hydratation post-séance : QUALITATIF, sans chiffre. Aucune source publique ne
// donne de formule « kcal brûlées → ml d'eau » ; on affirme seulement le fait sourcé que
// les besoins hydriques augmentent avec l'activité physique.
export const HYDRATION_EXERCISE_NOTE = {
  text: "Séance enregistrée : pense à boire davantage — les besoins en eau augmentent avec l'activité physique et la transpiration.",
  source: 'EFSA 2010 (valeurs de référence eau)',
} as const;

// Hydratation : ~30–40 ml/kg/jour est une RÈGLE EMPIRIQUE clinique, pas une reco
// OMS/EFSA (EFSA exprime l'apport en volume absolu : ~2,0 L/j F, 2,5 L/j H).
export const WATER_ML_PER_KG_RECO: Guideline = {
  min: 30,
  max: 40,
  source: 'règle empirique, non OMS/EFSA',
};
